import { jsPDF } from 'jspdf';
import Logo from '../../../assets/Logo-Negro.png';
import FirmaGerenciaRRHH from '../../../assets/firmaGerenciaRRHH.jpeg';

/**
 * Formatea una fecha YYYY-MM-DD a formato DD/MM/YYYY sin problemas de timezone
 */
const formatearFechaSinTimezone = (fechaString) => {
  if (!fechaString) return '';
  if (typeof fechaString === 'string' && fechaString.includes('/')) return fechaString;
  if (typeof fechaString === 'string' && fechaString.includes('-')) {
    const [year, month, day] = fechaString.split('-');
    return `${day}/${month}/${year}`;
  }
  if (fechaString instanceof Date) {
    const day = String(fechaString.getDate()).padStart(2, '0');
    const month = String(fechaString.getMonth() + 1).padStart(2, '0');
    const year = fechaString.getFullYear();
    return `${day}/${month}/${year}`;
  }
  return '';
};

/**
 * Genera el PDF del Informe Disciplinario (F073), usado por el motivo "Generales".
 * Header propio, datos del empleado y una única caja libre con el texto que
 * carga quien realiza el informe (sin notificación fija ni descargo: estos
 * informes vienen más detallados por sí mismos).
 * Lleva solo 2 firmas: Empleado Notificado y Gerencia/RRHH (sin Inspector).
 */
export const generateInformeDisciplinarioPDF = async ({
  informeNumero = '',
  lugarControl = '',
  fecha = '',
  hora = '',
  motivoDetalle = '',
  empleado = '',
  legajo = '',
  firmaEmpleadoBase64 = ''
}) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const marginLeft = 15;
    const marginRight = 15;
    const contentWidth = pageWidth - marginLeft - marginRight;
    let y = 15;

    // ==================== ENCABEZADO ESTILO TABLA ====================
    const headerHeight = 26;
    const col1Width = 50;
    const col3Width = 50;
    const col2Width = pageWidth - col1Width - col3Width;

    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.rect(0, 0, pageWidth, headerHeight);
    doc.line(col1Width, 0, col1Width, headerHeight);
    doc.line(pageWidth - col3Width, 0, pageWidth - col3Width, headerHeight);

    const logoImg = new Image();
    logoImg.src = Logo;
    await new Promise(resolve => { logoImg.onload = resolve; });
    const logoWidth = 30;
    const logoHeight = 14;
    doc.addImage(logoImg, 'PNG', (col1Width - logoWidth) / 2, (headerHeight - logoHeight) / 2, logoWidth, logoHeight);

    const firmaGerenciaImg = new Image();
    firmaGerenciaImg.src = FirmaGerenciaRRHH;
    await new Promise(resolve => { firmaGerenciaImg.onload = resolve; });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text('INFORMES DISCIPLINARIOS', col1Width + col2Width / 2, headerHeight / 2 + 2, { align: 'center' });

    // (con margen respecto al borde superior/inferior y a la línea separadora)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    const col3X = pageWidth - col3Width + 7;
    let col3Y = 10;
    doc.text('F073', col3X, col3Y);
    col3Y += 6.5;
    doc.text('VERSIÓN 01', col3X, col3Y);
    col3Y += 6.5;
    doc.text('REVISIÓN 23/09/26', col3X, col3Y);

    y = headerHeight + 8;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(`N° ${String(informeNumero || '0').padStart(4, '0')}`, pageWidth - marginRight, y, { align: 'right' });

    y += 5;
    doc.setLineWidth(0.8);
    doc.line(marginLeft, y, pageWidth - marginRight, y);
    y += 8;

    // ==================== DATOS DEL INFORME ====================
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);

    const fechaFormateada = formatearFechaSinTimezone(fecha);
    const col1X = marginLeft;
    const col2X = pageWidth / 2 + 5;
    const lineHeight = 6;

    if (lugarControl) {
      doc.setFont('helvetica', 'bold');
      doc.text('Lugar:', col1X, y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(lugarControl), col1X + 20, y);
    }

    doc.setFont('helvetica', 'bold');
    doc.text('Fecha:', col2X, y);
    doc.setFont('helvetica', 'normal');
    doc.text(fechaFormateada, col2X + 15, y);
    y += lineHeight;

    if (hora) {
      doc.setFont('helvetica', 'bold');
      doc.text('Hora:', col1X, y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(hora), col1X + 20, y);
      y += lineHeight;
    }
    y += 2;

    // ==================== DATOS DEL EMPLEADO ====================
    doc.setLineWidth(0.3);
    doc.line(marginLeft, y, pageWidth - marginRight, y);
    y += 5;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('DATOS DEL EMPLEADO', marginLeft, y);
    y += 6;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Legajo N°:', col1X, y);
    doc.setFont('helvetica', 'normal');
    doc.text(String(legajo || ''), col1X + 20, y);

    doc.setFont('helvetica', 'bold');
    doc.text('Nombre:', col2X, y);
    doc.setFont('helvetica', 'normal');
    doc.text(String(empleado || ''), col2X + 18, y);
    y += lineHeight + 3;

    // ==================== CAJA LIBRE ====================
    // Un solo bloque con el texto que escribió quien generó el informe.
    // Sin descargo: estos informes ya vienen detallados por quien los carga.
    doc.setLineWidth(0.3);
    doc.line(marginLeft, y, pageWidth - marginRight, y);
    y += 5;

    const firmaAreaHeight = 32;
    const margenInferior = 12;
    const firmasY = pageHeight - margenInferior - firmaAreaHeight;
    const cajaHeight = Math.max(firmasY - y - 5, 20);

    doc.setLineWidth(0.3);
    doc.rect(marginLeft, y, contentWidth, cajaHeight);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    if (motivoDetalle && String(motivoDetalle).trim()) {
      const motivoLines = doc.splitTextToSize(String(motivoDetalle), contentWidth - 6);
      doc.text(motivoLines, marginLeft + 3, y + 6);
    }

    // ==================== FIRMAS (Empleado y RRHH, sin Inspector) ====================
    y = firmasY;
    const firmaWidth = (contentWidth - 10) / 2;

    // Firma Empleado Notificado
    const firma1X = marginLeft;
    if (firmaEmpleadoBase64) {
      try {
        doc.addImage(firmaEmpleadoBase64, 'PNG', firma1X + 5, y, firmaWidth - 10, 20);
      } catch (e) {
        console.warn('Error al cargar firma empleado:', e);
      }
    }
    doc.setLineWidth(0.3);
    doc.line(firma1X, y + firmaAreaHeight - 12, firma1X + firmaWidth, y + firmaAreaHeight - 12);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('FIRMA Y ACLARACIÓN', firma1X + firmaWidth / 2, y + firmaAreaHeight - 7, { align: 'center' });
    doc.text('EMPLEADO NOTIFICADO', firma1X + firmaWidth / 2, y + firmaAreaHeight - 3, { align: 'center' });

    // Firma Gerencia/RRHH
    const firma2X = marginLeft + firmaWidth + 10;
    try {
      doc.addImage(firmaGerenciaImg, 'JPEG', firma2X + 5, y, firmaWidth - 10, 20);
    } catch (e) {
      console.warn('Error al cargar firma gerencia:', e);
    }
    doc.line(firma2X, y + firmaAreaHeight - 12, firma2X + firmaWidth, y + firmaAreaHeight - 12);
    doc.text('FIRMA Y ACLARACIÓN', firma2X + firmaWidth / 2, y + firmaAreaHeight - 7, { align: 'center' });
    doc.text('GERENCIA / RRHH', firma2X + firmaWidth / 2, y + firmaAreaHeight - 3, { align: 'center' });

    // ==================== GENERAR PDF ====================
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');

    return { blob: pdfBlob, url: pdfUrl };
  } catch (error) {
    console.error('Error al generar el PDF del informe disciplinario:', error);
    throw error;
  }
};
